import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayName, getBestProfilePhoto } from '../../models/User';

export const UserMenu: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { currentUser, userProfile, isBusiness, logout } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleLogout = async () => {
        handleMenuClose();
        await logout();
        navigate('/');
    };

    if (!currentUser) return null;

    return (
        <>
            <IconButton
                onClick={handleMenuOpen}
                sx={{
                    p: 0.5,
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'scale(1.05)', bgcolor: 'action.hover' },
                }}
            >
                <Avatar
                    src={userProfile ? getBestProfilePhoto(userProfile) : undefined}
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.primary.main,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                    }}
                >
                    {userProfile?.firstName?.[0]?.toUpperCase() || 'U'}
                </Avatar>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 1.5,
                        borderRadius: 2, // Slightly rounded as requested
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        minWidth: 200,
                        overflow: 'visible',
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                        {userProfile ? getDisplayName(userProfile) : 'User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {currentUser.email}
                    </Typography>
                </Box>
                <Divider />

                {/* Home */}
                <MenuItem
                    onClick={() => { handleMenuClose(); navigate('/'); }}
                    sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <ListItemIcon>
                        <HomeIcon
                            fontSize="small"
                            color={location.pathname === '/' ? 'primary' : 'inherit'}
                        />
                    </ListItemIcon>
                    <Typography
                        variant="body2"
                        fontWeight={location.pathname === '/' ? 600 : 400}
                        color={location.pathname === '/' ? 'primary.main' : 'text.primary'}
                    >
                        Home
                    </Typography>
                </MenuItem>

                {/* Dashboard - Only for business users */}
                {isBusiness && (location.pathname !== '/' || isMobile) && (
                    <MenuItem
                        onClick={() => { handleMenuClose(); navigate('/business/catalogs'); }}
                        sx={{
                            py: 1.5,
                            '&:hover': { bgcolor: 'action.hover' }
                        }}
                    >
                        <ListItemIcon>
                            <DashboardIcon
                                fontSize="small"
                                color={(location.pathname.startsWith('/business') && !location.pathname.startsWith('/business/profile')) ? 'primary' : 'inherit'}
                            />
                        </ListItemIcon>
                        <Typography
                            variant="body2"
                            fontWeight={(location.pathname.startsWith('/business') && !location.pathname.startsWith('/business/profile')) ? 600 : 400}
                            color={(location.pathname.startsWith('/business') && !location.pathname.startsWith('/business/profile')) ? 'primary.main' : 'text.primary'}
                        >
                            Dashboard
                        </Typography>
                    </MenuItem>
                )}

                {/* Profile */}
                <MenuItem
                    onClick={() => { handleMenuClose(); navigate('/business/profile'); }}
                    sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <ListItemIcon>
                        <PersonIcon
                            fontSize="small"
                            color={location.pathname.startsWith('/business/profile') ? 'primary' : 'inherit'}
                        />
                    </ListItemIcon>
                    <Typography
                        variant="body2"
                        fontWeight={location.pathname.startsWith('/business/profile') ? 600 : 400}
                        color={location.pathname.startsWith('/business/profile') ? 'primary.main' : 'text.primary'}
                    >
                        Profile
                    </Typography>
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout} sx={{ color: 'error.main', py: 1.5 }}>
                    <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </>
    );
};
